'use client';

import type { ReactNode } from 'react';
import { AlertTriangle, Ban, CalendarClock, Clock, Coins, ScrollText, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * Normativa oficial de la Semana Santa de Jartiland 2026, escrita a mano en la
 * web (antes solo existía en un PDF). Va en el panel de Jartinet, debajo del
 * Discord de la Junta de Hermandades.
 *
 * Sin plegables a propósito: es lo que se tiene que cumplir, así que se lee de
 * arriba abajo sin tener que abrir nada.
 */

const LIMITES_HORARIOS = [
  {
    tipo: 'Hermandades',
    limite: '3 h 30 min',
    detalle: 'de la Cruz de Guía en la calle, sin contar el tiempo dentro de la Catedral.',
  },
  {
    tipo: 'Pro-hermandades',
    limite: '2 h 30 min',
    detalle: 'de la Cruz de Guía en la calle, sin contar el tiempo dentro de la iglesia donde hacen estación de penitencia.',
  },
  {
    tipo: 'Grupos Parroquiales',
    limite: '2 h',
    detalle: 'de la Cruz de Guía en la calle, sin contar el tiempo dentro de la iglesia donde hacen estación de penitencia.',
  },
];

const FILAS = [
  {
    tipo: 'Hermandades',
    gratis: '20 filas gratis',
    tramos: [
      'Más de 30 hermanos: límite de 30 filas',
      'Más de 40 hermanos: límite de 40 filas',
      'Más de 50 hermanos: límite de 50 filas',
    ],
  },
  {
    tipo: 'Pro-hermandades',
    gratis: '15 filas gratis',
    tramos: [
      'Más de 25 hermanos: límite de 25 filas',
      'Más de 30 hermanos: límite de 35 filas',
      'Más de 35 hermanos: límite de 40 filas',
    ],
  },
  {
    tipo: 'Grupos Parroquiales',
    gratis: '10 filas gratis',
    tramos: [
      'Más de 15 hermanos: límite de 15 filas',
      'Más de 20 hermanos: límite de 20 filas',
      'Más de 25 hermanos: límite de 25 filas',
    ],
  },
];

/** Normas generales. `fecha` marca las que tienen día límite. */
const NORMAS: { texto: string; fecha?: string }[] = [
  { texto: 'No se podrá retrasar la hora de salida.' },
  { texto: 'Último día para enviar itinerarios, cuadros de horarios y firmar banda.', fecha: '19 de agosto' },
  {
    texto:
      'Si el hermano mayor no está a la hora de salida, deberá comunicar antes de esa hora si se cancela la estación de penitencia, si la cofradía sale sin él o si sale sin él y se incorpora más tarde.',
  },
  { texto: 'La cofradía deberá estar preparada 15 minutos antes de la hora de salida.' },
  {
    texto:
      'No está permitida ninguna decoración que no sean balconeras rojas (si una cofradía tiene balconeras personalizadas, puede poner algunas por su feligresía).',
  },
  { texto: 'La hermandad no podrá estar más de 10 minutos dentro de la Catedral.' },
  { texto: 'Último día para enviar los cortejos por ticket, para que administración los revise y acepte.', fecha: '23 de agosto' },
  { texto: 'Solo las hermandades y pro-hermandades pueden usar hábito nazareno.' },
  { texto: 'No están permitidas las petaladas o similares al azar: las organiza la cofradía en puntos concretos.' },
  {
    texto:
      'Todas las cofradías deberán emitir papeleta de sitio desde la Jartinet a cada persona que participe, indicando el puesto que ejercerá.',
    fecha: '23 de agosto',
  },
];

const SANCIONES = [
  { causa: 'Sin banda firmada el 19 de agosto', castigo: 'La cofradía irá en silencio.' },
  {
    causa: 'Sin recorrido ni cuadro de horarios el 19 de agosto',
    castigo: 'Multa de 300 jartis de oro. Si tres días después sigue sin enviarse, la cofradía no saldrá.',
    grave: true,
  },
  {
    causa: 'Superar el límite horario en la calle',
    castigo: 'Sanción gradual en jartis de oro según el tiempo de más. Avisamos: son cantidades altas.',
  },
  {
    causa: 'Cortejo que no cumple la normativa el día de la salida',
    castigo: 'Sanción de gran cantidad de jartis de oro; incluso se puede determinar que la cofradía no salga.',
  },
  { causa: 'No salir a la hora', castigo: 'Sanción de altas cantidades de jartis de oro por cada minuto de retraso.' },
  {
    causa: 'Hermano mayor ausente sin comunicar su decisión',
    castigo: 'El Consejo de Hermandades dictaminará si la cofradía sale o no.',
  },
  { causa: 'Petalada no autorizada', castigo: 'Sanción de altas cantidades de jartis de oro.' },
  { causa: 'Papeletas de sitio sin enviar el 23 de agosto', castigo: 'La cofradía no saldrá.', grave: true },
];

function Seccion({ icon, titulo, children }: { icon: ReactNode; titulo: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
        {icon}
        {titulo}
      </h3>
      {children}
    </div>
  );
}

export function NormativaSemanaSanta() {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-white/8 bg-gradient-to-br from-primary/12 to-transparent">
        <CardTitle className="flex items-center gap-2 text-base">
          <ScrollText className="h-5 w-5 flex-shrink-0 text-primary" />
          Normativa Semana Santa 2026
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          De obligado cumplimiento para todas las cofradías de Jartiland.
        </p>
      </CardHeader>

      <CardContent className="space-y-6 pt-5">
        {/* ── Límites horarios ── */}
        <Seccion icon={<Clock className="h-3.5 w-3.5" />} titulo="Límites horarios">
          <div className="space-y-2">
            {LIMITES_HORARIOS.map((l) => (
              <div key={l.tipo} className="rounded-2xl border border-white/8 bg-muted/40 p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold">{l.tipo}</span>
                  <span className="flex-shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-black text-primary">
                    {l.limite}
                  </span>
                </div>
                <p className="mt-1 text-xs leading-snug text-muted-foreground">{l.detalle}</p>
              </div>
            ))}
          </div>
        </Seccion>

        {/* ── Longitud de los cortejos ── */}
        <Seccion icon={<Users className="h-3.5 w-3.5" />} titulo="Longitud de los cortejos">
          <div className="space-y-1.5 rounded-2xl border border-amber-500/25 bg-amber-500/8 p-3 text-xs leading-snug text-muted-foreground">
            <p className="flex items-center gap-1.5 font-semibold text-amber-300">
              <Coins className="h-3.5 w-3.5 flex-shrink-0" />
              Cada fila extra cuesta 10 jartis de oro
            </p>
            <p>
              Los números son <strong className="text-foreground">por cada paso</strong> y cuentan el
              <strong className="text-foreground"> total de filas</strong>, incluidas las de los cuerpos de acólitos.
            </p>
            <p>
              No vale repartirlas a capricho entre pasos (por ejemplo 10 en uno y 90 en el otro): el límite es el mismo
              para cada paso.
            </p>
          </div>

          <div className="space-y-2">
            {FILAS.map((f) => (
              <div key={f.tipo} className="rounded-2xl border border-white/8 bg-muted/40 p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold">{f.tipo}</span>
                  <span className="flex-shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[0.7rem] font-bold text-emerald-400">
                    {f.gratis}
                  </span>
                </div>
                <ul className="mt-2 space-y-1">
                  {f.tramos.map((t) => (
                    <li key={t} className="flex items-start gap-1.5 text-xs text-muted-foreground">
                      <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-primary/70" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Seccion>

        {/* ── Normas generales ── */}
        <Seccion icon={<CalendarClock className="h-3.5 w-3.5" />} titulo="Normas generales">
          <ul className="space-y-2">
            {NORMAS.map((n) => (
              <li key={n.texto} className="flex items-start gap-2 text-xs leading-snug text-muted-foreground">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                <span>
                  {n.fecha && (
                    <strong className="mr-1.5 rounded bg-sky-500/15 px-1.5 py-px text-[0.7rem] font-bold text-sky-300">
                      {n.fecha}
                    </strong>
                  )}
                  {n.texto}
                </span>
              </li>
            ))}
          </ul>
        </Seccion>

        {/* ── Sanciones ── */}
        <Seccion icon={<AlertTriangle className="h-3.5 w-3.5" />} titulo="Sanciones">
          <div className="space-y-2">
            {SANCIONES.map((s) => (
              <div
                key={s.causa}
                className={cn(
                  'relative overflow-hidden rounded-2xl border p-3 pl-4',
                  s.grave ? 'border-destructive/40 bg-destructive/10' : 'border-white/8 bg-muted/40',
                )}
              >
                <span className={cn('absolute inset-y-0 left-0 w-1', s.grave ? 'bg-destructive' : 'bg-rose-500/50')} />
                <p className="flex items-start gap-1.5 text-xs font-semibold">
                  <Ban className={cn('mt-px h-3.5 w-3.5 flex-shrink-0', s.grave ? 'text-destructive' : 'text-rose-400')} />
                  {s.causa}
                </p>
                <p className="mt-1 text-xs leading-snug text-muted-foreground">{s.castigo}</p>
              </div>
            ))}
          </div>
        </Seccion>
      </CardContent>
    </Card>
  );
}
